import { useMemo } from 'react'

import { zeroAddress } from 'viem'

import { SupportedChainId } from '~/constants/chains'
import { useTransactionCost } from '~/hooks/transactions/useTransactionCost'
import { useSmartAccountBalances } from '~/hooks/useBalances'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { BackendTransactionCall } from '~/types'

/**
 * Warns when the account pays its own gas and its native balance cannot cover
 * the estimated gas plus the native value the calls send out.
 */
export const useInsufficientGasWarning = ({
  smartAccountId,
  chainId,
  calls,
  transactionCost,
  isSponsored,
}: {
  smartAccountId: string
  chainId: SupportedChainId
  calls: BackendTransactionCall[]
  transactionCost: ReturnType<typeof useTransactionCost>
  isSponsored: boolean
}) => {
  const { smartAccounts } = useSmartAccounts()
  const senderAccount = smartAccounts.find(
    (account) => account.id === smartAccountId,
  )

  const { balances, hasFetchedBalances } = useSmartAccountBalances(
    senderAccount?.address,
    { showSpamTokens: true },
  )
  // The balances endpoint omits zero rows, so a fetched list with no native
  // row means the account holds none. While loading, after a failed first
  // fetch, or with no sender the list is empty but unknown.
  const nativeTokenBalance = hasFetchedBalances
    ? (balances.find(
        (balance) =>
          balance.chainId === chainId && balance.address === zeroAddress,
      )?.amount ?? 0n)
    : undefined

  const nativeValueOut = calls.reduce((sum, call) => sum + call.value, 0n)

  const showWarning = useMemo(() => {
    if (
      isSponsored ||
      !transactionCost.transactionGas ||
      nativeTokenBalance === undefined
    )
      return false

    // Allow for 5% buffer on gas cost to account for gas price fluctuations
    const gasWithMargin = (transactionCost.transactionGas * 105n) / 100n
    return nativeTokenBalance - gasWithMargin - nativeValueOut < 0n
  }, [
    nativeTokenBalance,
    isSponsored,
    transactionCost.transactionGas,
    nativeValueOut,
  ])

  return { showWarning }
}
